import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CountriesDropdown } from "../components/dropdowns/CountriesDropdown";
import { CurrencyLogoDropdown } from "../components/dropdowns/CurrencyLogoDropdown";
import DateOfBirthPicker from "../components/inputs/DateOfBirthPicker";
import GlobalTextInputField from "../components/inputs/GlobalTextInputField";
import AccessInfoBadges from "../components/layout/AccessInfoBadges";
import ExchangeInfoPanel from "../components/layout/ExchangeInfoPanel";
import { commonApiServices } from "../services/api/Common";
import { primeFiatApiServices } from "../services/api/PrimeFiat";
import { GetCountriesListData } from "../services/models/GetCountriesList";
import { GetTokenListData } from "../services/models/GetTokenList";
import { CALLBACK_URL, REDIRECT_URL } from "../utils/globals";
import { isValidDobDate } from "../utils/dob";

const FIAT_CURRENCIES = [
  {
    id: "CAD",
    currency: "CAD",
    currencyLogo: "https://assets.tylt.money/country-images/CAN.png",
  },
  {
    id: "EUR",
    currency: "EUR",
    currencyLogo: "https://assets.tylt.money/holdings-icons/EUR.png",
  },
];

const PROVINCE_CODES = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

const PROVINCE_OPTIONS = Object.entries(PROVINCE_CODES).map(([code, name]) => ({
  id: code,
  country: name,
}));

const CHANNEL_OPTIONS = [
  { id: 2, country: "Gigadat" },
  { id: 1, country: "Apaylo" },
];

type Flow = "onramp" | "offramp";

function formatPayoutAmount(value: string) {
  const [whole, fraction = ""] = value.split(".");
  return `${whole || "0"}.${(fraction + "00").slice(0, 2)}`;
}

function isValidIban(value: string) {
  const iban = value.replace(/\s+/g, "").toUpperCase();
  return /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban);
}

const dropdownProps = {
  color: "bg-page-body",
  baseColor: "bg-page-body",
  borderColor: "border-border-subtle",
  expandedBorderColor: "border-border-subtle",
  titleColor: "text-text-subtle",
};

function SectionTitle({ children }: { children: string }) {
  return (
    <h2 className="text-[#1418A8] text-[12px] font-bold mb-[10px]">
      {children}
    </h2>
  );
}

function HomePage() {
  const navigate = useNavigate();

  useEffect(() => {
    const savedInstanceId = sessionStorage.getItem("obd_instance_id");
    const savedRegion = sessionStorage.getItem("obd_region") || "eur";
    if (savedInstanceId) {
      navigate(`/review/${savedRegion}/${savedInstanceId}`, { replace: true });
    }
  }, [navigate]);

  const [countries, setCountries] = useState<GetCountriesListData[]>([]);
  const [tokens, setTokens] = useState<GetTokenListData[]>([]);
  const [loadingLists, setLoadingLists] = useState(true);

  const [flow, setFlow] = useState<Flow>("onramp");
  const [fiatCurrency, setFiatCurrency] = useState<string | undefined>();
  const [channel, setChannel] = useState<1 | 2>(2);
  const [assetId, setAssetId] = useState<string | number | undefined>();
  const [amount, setAmount] = useState("");
  const [networkId, setNetworkId] = useState<string | number | undefined>();
  const [walletAddress, setWalletAddress] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [residenceCountryId, setResidenceCountryId] = useState<
    string | number | undefined
  >();
  const [birthCountryId, setBirthCountryId] = useState<
    string | number | undefined
  >();
  const [mobile, setMobile] = useState("");
  const [province, setProvince] = useState<string | number | undefined>();
  const [iban, setIban] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoadingLists(true);
      try {
        const [countriesRes, tokensRes] = await Promise.all([
          commonApiServices.getCountriesList({ isEuropeFlow: true }),
          commonApiServices.getTokenList().catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setCountries(countriesRes.data || []);
        setTokens(tokensRes.data?.length ? tokensRes.data : []);
      } catch {
      } finally {
        if (!cancelled) setLoadingLists(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedToken = useMemo(
    () => tokens.find((token) => token.currencyId === assetId),
    [tokens, assetId],
  );

  const selectedNetwork = useMemo(
    () =>
      selectedToken?.networks?.find(
        (network) => network.networkId === networkId,
      ),
    [selectedToken, networkId],
  );

  const isWalletAddressValid = useMemo(() => {
    if (!walletAddress.trim() || !selectedNetwork?.networkRegex) return true;
    try {
      return new RegExp(selectedNetwork.networkRegex).test(
        walletAddress.trim(),
      );
    } catch {
      return true;
    }
  }, [walletAddress, selectedNetwork]);

  const assetOptions = useMemo(() => {
    const options = tokens.map((token) => ({
      id: token.currencyId,
      currency: token.currencySymbol,
      currencyLogo: token.currencyImg,
      currencyName: token.currencyName,
      isToken: true,
      isReceive: true,
    }));
    if (flow !== "offramp") return options;
    const stablecoins = options.filter((token) =>
      ["USDC", "USDT"].includes(token.currency.toUpperCase()),
    );
    return stablecoins.length ? stablecoins : options;
  }, [tokens, flow]);

  const networkOptions = useMemo(() => {
    const networks = selectedToken?.networks?.length
      ? selectedToken.networks
      : [];
    return networks.map((network) => ({
      id: network.networkId,
      currency: network.networkSymbol || network.networkName,
      currencyLogo: network.networkImg,
    }));
  }, [selectedToken]);

  const offrampNetworkOptions = useMemo(() => {
    const allowed = /^(BSC|BEP20|POL|POLYGON|MATIC|ETH|ERC20)$/i;
    const matched = networkOptions.filter((network) =>
      allowed.test(String(network.currency || "")),
    );
    return matched.length ? matched : networkOptions;
  }, [networkOptions]);

  useEffect(() => {
    setNetworkId(undefined);
    setWalletAddress("");
  }, [assetId]);

  useEffect(() => {
    if (flow !== "offramp" || assetId == null) return;
    if (!assetOptions.some((option) => option.id === assetId)) {
      setAssetId(undefined);
    }
  }, [flow, assetOptions, assetId]);

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (/^\d*\.?\d*$/.test(next)) {
      setAmount(next);
    }
  };

  const handleSubmit = async () => {
    setError("");

    const residenceCountry = countries.find(
      (country) => country.id === residenceCountryId,
    );
    const birthCountry = countries.find(
      (country) => country.id === birthCountryId,
    );

    const isCad = fiatCurrency === "CAD";
    const isOfframp = flow === "offramp";
    const mobileDigits = mobile.replace(/\D/g, "");
    const normalizedIban = iban.replace(/\s+/g, "").toUpperCase();

    if (
      !fiatCurrency ||
      !selectedToken ||
      !amount ||
      Number(amount) <= 0 ||
      !selectedNetwork ||
      (!isOfframp && !walletAddress) ||
      !firstName ||
      !lastName ||
      !email ||
      !dob ||
      !isValidDobDate(dob) ||
      !residenceCountry ||
      !birthCountry ||
      (isCad && (!mobileDigits || !province)) ||
      (isOfframp && isCad && mobileDigits.length !== 10) ||
      (isOfframp && !isCad && !normalizedIban)
    ) {
      setError(
        !dob || !isValidDobDate(dob)
          ? "Please enter a valid date of birth (18+)."
          : isOfframp && isCad && mobileDigits && mobileDigits.length !== 10
            ? "Mobile number must be exactly 10 digits."
            : "Please fill in all fields.",
      );
      return;
    }

    if (!isOfframp && !isWalletAddressValid) {
      setError(
        `Invalid wallet address for ${selectedNetwork?.networkName || "the selected network"}.`,
      );
      return;
    }

    if (isOfframp && !isCad && !isValidIban(normalizedIban)) {
      setError("Please enter a valid IBAN.");
      return;
    }

    setSubmitting(true);
    try {
      const merchantOrderId = `obd-${Date.now()}`;
      const region = fiatCurrency === "EUR" ? "eur" : "cad";
      const merchantDetails = {
        merchantName: "TYLT Exchange",
        merchantUrl: window.location.origin,
        merchantInternalId: `merchant.${Date.now()}`,
      };

      const response = isOfframp
        ? await primeFiatApiServices.createPayout(
            {
              amount: formatPayoutAmount(amount),
              merchantOrderId,
              currencySymbol: String(fiatCurrency),
              cryptoCurrencySymbol: selectedToken.currencySymbol,
              channel: isCad ? channel : 1,
              transferType: "external",
              networkSymbol: selectedNetwork.networkSymbol,
              callBackUrl: CALLBACK_URL,
              redirectUrl: REDIRECT_URL,
              merchantDetails,
              userDetails: {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                country: residenceCountry.countryShort2,
                countryOfBirth: birthCountry.countryShort2,
                dob,
                ...(isCad
                  ? {
                      mobile: mobileDigits,
                      province: String(province),
                    }
                  : {}),
              },
              ...(isCad ? {} : { payeeDetails: { iban: normalizedIban } }),
            },
            fiatCurrency,
          )
        : await primeFiatApiServices.createPayin(
            {
              merchantOrderId,
              callBackUrl: CALLBACK_URL,
              redirectUrl: REDIRECT_URL,
              amount: Number(amount),
              currencySymbol: String(fiatCurrency),
              cryptoCurrencySymbol: selectedToken.currencySymbol,
              cryptoUI: 1,
              ...(isCad ? { channel } : {}),
              merchantDetails,
              userDetails: {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                country: residenceCountry.countryShort2,
                countryOfBirth: birthCountry.countryShort2,
                dob,
                ...(isCad
                  ? {
                      mobile: mobile.trim(),
                      province: String(province),
                    }
                  : {}),
              },
              walletDetails: {
                address: walletAddress.trim(),
                network: selectedNetwork.networkSymbol,
              },
              transferType: "external",
            },
            fiatCurrency,
          );

      const instanceId = response.data?.instanceId;
      if (!instanceId) {
        setError(
          response.msg ||
            `Failed to create ${isOfframp ? "payout" : "purchase"} instance.`,
        );
        return;
      }

      sessionStorage.setItem("obd_instance_id", instanceId);
      sessionStorage.setItem("obd_region", region);
      sessionStorage.setItem("obd_flow", flow);
      if (isOfframp && isCad) {
        sessionStorage.setItem("obd_payout_mobile", mobileDigits);
        sessionStorage.setItem("obd_payout_province", String(province));
      } else {
        sessionStorage.removeItem("obd_payout_mobile");
        sessionStorage.removeItem("obd_payout_province");
      }
      navigate(`/review/${region}/${instanceId}`);
    } catch (err: any) {
      setError(
        err?.response?.data?.msg ||
          err?.message ||
          `Failed to create ${isOfframp ? "payout" : "purchase"}. Please try again.`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative h-dvh w-full overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <div className="noise w-full h-full" />
      </div>

      <div className="relative h-full w-full flex items-stretch justify-center px-[16px] lg:px-[80px] py-[20px] lg:py-[40px] overflow-hidden">
        <div className="w-full h-full flex flex-col lg:flex-row gap-[24px] justify-between items-stretch min-h-0">
          <ExchangeInfoPanel
            headline={
              flow === "offramp" ? (
                <>
                  Cash out crypto in less than{" "}
                  <span className="text-[#2E9B4F]">5 minutes!</span>
                </>
              ) : (
                <>
                  Buy Crypto in less than{" "}
                  <span className="text-[#2E9B4F]">5 minutes!</span>
                </>
              )
            }
          />

          <div
            className="w-full max-w-[560px] mx-auto lg:mx-0 flex-1 min-h-0 bg-bg-page-body rounded-[20px] flex flex-col overflow-hidden"
            style={{ boxShadow: "5px 5px 25px 0 rgba(0, 0, 0, 0.10)" }}
          >
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="shrink-0 px-[24px] pt-[24px] pb-[12px] bg-bg-page-body">
                <div
                  className="grid grid-cols-2 gap-[8px] p-[4px] rounded-full bg-[#F4F4F8]"
                  role="tablist"
                  aria-label="Exchange direction"
                >
                  {(
                    [
                      { id: "onramp", label: "Onramp" },
                      { id: "offramp", label: "Offramp" },
                    ] as const
                  ).map((option) => {
                    const selected = flow === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => {
                          setFlow(option.id);
                          setError("");
                        }}
                        className={`h-[36px] rounded-full text-[13px] font-semibold ${
                          selected ? "text-white" : "text-[#1418A8]"
                        }`}
                        style={
                          selected
                            ? {
                                background:
                                  "linear-gradient(90deg, #0E18A7 0%, #5E0EAF 51%, #AE0DB2 100%)",
                              }
                            : undefined
                        }
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-[24px] pb-[24px] pt-[8px]">
                {flow === "offramp" ? (
                  <>
                    <div className="mb-[20px]">
                      <SectionTitle>Pay-In Details</SectionTitle>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                        <CurrencyLogoDropdown
                          listOfValues={assetOptions}
                          value={assetId}
                          onChange={setAssetId}
                          label="Pay-In"
                          placeholder={
                            loadingLists ? "Loading..." : "Select crypto asset"
                          }
                          enableSearch
                          searchableValues={assetOptions.map((item) => [
                            item.currency,
                            item.currencyName || "",
                          ])}
                          {...dropdownProps}
                        />
                        <CurrencyLogoDropdown
                          listOfValues={offrampNetworkOptions}
                          value={networkId}
                          onChange={setNetworkId}
                          label="Network"
                          placeholder={
                            !assetId
                              ? "Select asset first"
                              : networkOptions.length
                                ? "Select network"
                                : "No networks available"
                          }
                          {...dropdownProps}
                        />
                      </div>
                    </div>

                    <div className="mb-[20px]">
                      <SectionTitle>Payout Details</SectionTitle>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                        <CurrencyLogoDropdown
                          listOfValues={FIAT_CURRENCIES}
                          value={fiatCurrency}
                          onChange={(value) => setFiatCurrency(value as string)}
                          label="Receive"
                          placeholder="Select currency"
                          {...dropdownProps}
                        />
                        <GlobalTextInputField
                          title="Amount"
                          placeholder="Enter amount"
                          value={amount}
                          onChange={handleAmountChange}
                          inputBorder="border-border-subtle rounded-lg"
                        />
                        <GlobalTextInputField
                          title="Payment Method"
                          placeholder="Enter payment method"
                          value={
                            fiatCurrency === "EUR"
                              ? "Open Banking"
                              : fiatCurrency === "CAD"
                                ? "Interac e-Transfer"
                                : "Select fiat currency"
                          }
                          readonly={true}
                          onChange={() => {}}
                          inputBorder="border-border-subtle rounded-lg"
                        />
                        {fiatCurrency === "CAD" ? (
                          <CountriesDropdown
                            listOfValues={CHANNEL_OPTIONS}
                            value={channel}
                            onChange={(value) =>
                              setChannel(Number(value) === 1 ? 1 : 2)
                            }
                            label="Channel"
                            placeholder="Gigadat"
                            {...dropdownProps}
                          />
                        ) : null}
                        {fiatCurrency === "EUR" ? (
                          <div>
                            <GlobalTextInputField
                              title="IBAN"
                              placeholder="Enter IBAN"
                              value={iban}
                              onChange={(e) =>
                                setIban(e.target.value.toUpperCase())
                              }
                              inputBorder={
                                iban && !isValidIban(iban)
                                  ? "border-border-button-delete rounded-lg"
                                  : "border-border-subtle rounded-lg"
                              }
                            />
                            {iban && !isValidIban(iban) ? (
                              <p className="mt-[6px] text-[10px] text-text-status-red">
                                Enter a valid IBAN
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-[20px]">
                      <SectionTitle>Payment Information</SectionTitle>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                        <CurrencyLogoDropdown
                          listOfValues={FIAT_CURRENCIES}
                          value={fiatCurrency}
                          onChange={(value) => setFiatCurrency(value as string)}
                          label="Pay With"
                          placeholder="Select fiat currency"
                          {...dropdownProps}
                        />
                        <GlobalTextInputField
                          title="Payment Method"
                          placeholder="Enter payment method"
                          value={
                            fiatCurrency === "EUR"
                              ? "Open Banking"
                              : fiatCurrency === "CAD"
                                ? "Interac e-Transfer"
                                : "Select fiat currency"
                          }
                          readonly={true}
                          onChange={() => {}}
                          inputBorder="border-border-subtle rounded-lg"
                        />
                        {fiatCurrency === "CAD" ? (
                          <CountriesDropdown
                            listOfValues={CHANNEL_OPTIONS}
                            value={channel}
                            onChange={(value) =>
                              setChannel(Number(value) === 1 ? 1 : 2)
                            }
                            label="Channel"
                            placeholder="Gigadat"
                            {...dropdownProps}
                          />
                        ) : null}
                      </div>
                    </div>

                    <div className="mb-[20px]">
                      <SectionTitle>Purchase Details</SectionTitle>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                        <CurrencyLogoDropdown
                          listOfValues={assetOptions}
                          value={assetId}
                          onChange={setAssetId}
                          label="Receive Asset"
                          placeholder={
                            loadingLists ? "Loading..." : "Select crypto asset"
                          }
                          enableSearch
                          searchableValues={assetOptions.map((item) => [
                            item.currency,
                            item.currencyName || "",
                          ])}
                          {...dropdownProps}
                        />
                        <GlobalTextInputField
                          title="Enter Amount"
                          placeholder="Enter amount"
                          value={amount}
                          onChange={handleAmountChange}
                          inputBorder="border-border-subtle rounded-lg"
                        />
                      </div>
                    </div>

                    <div className="mb-[20px]">
                      <SectionTitle>Wallet Details</SectionTitle>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                        <CurrencyLogoDropdown
                          listOfValues={networkOptions}
                          value={networkId}
                          onChange={setNetworkId}
                          label="Network"
                          placeholder={
                            !assetId
                              ? "Select asset first"
                              : networkOptions.length
                                ? "Select network"
                                : "No networks available"
                          }
                          {...dropdownProps}
                        />
                        <div>
                          <GlobalTextInputField
                            title="Wallet Address"
                            placeholder="Paste wallet address"
                            value={walletAddress}
                            onChange={(e) => setWalletAddress(e.target.value)}
                            inputBorder={
                              walletAddress && !isWalletAddressValid
                                ? "border-border-button-delete rounded-lg"
                                : "border-border-subtle rounded-lg"
                            }
                            suffixIcon={
                              <span className="material-symbols-outlined text-[18px] text-text-subtle">
                                content_paste
                              </span>
                            }
                            suffixIconOnClick={async () => {
                              try {
                                const text =
                                  await navigator.clipboard.readText();
                                if (text) setWalletAddress(text.trim());
                              } catch {}
                            }}
                          />
                          {walletAddress && !isWalletAddressValid ? (
                            <p className="mt-[6px] text-[10px] text-text-status-red">
                              Invalid address for{" "}
                              {selectedNetwork?.networkName || "this network"}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <SectionTitle>Identity Verification</SectionTitle>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px]">
                    <GlobalTextInputField
                      title="First Name"
                      placeholder="Enter first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      inputBorder="border-border-subtle rounded-lg"
                    />
                    <GlobalTextInputField
                      title="Last Name"
                      placeholder="Enter last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      inputBorder="border-border-subtle rounded-lg"
                    />
                    <GlobalTextInputField
                      title="Email"
                      type="email"
                      placeholder="Enter email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      inputBorder="border-border-subtle rounded-lg"
                    />
                    <DateOfBirthPicker value={dob} onChange={setDob} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px] mt-[12px]">
                    <CountriesDropdown
                      listOfValues={
                        fiatCurrency === "EUR"
                          ? countries.filter(
                              (country) => country.euEaaCountry === 0,
                            )
                          : countries
                      }
                      value={residenceCountryId}
                      onChange={setResidenceCountryId}
                      label="Country of Residence"
                      placeholder="Select country"
                      enableSearch
                      {...dropdownProps}
                    />
                    <CountriesDropdown
                      listOfValues={countries}
                      value={birthCountryId}
                      onChange={setBirthCountryId}
                      label="Country of Birth"
                      placeholder="Select country"
                      enableSearch
                      {...dropdownProps}
                    />
                  </div>
                  {fiatCurrency === "CAD" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px] mt-[12px]">
                      <GlobalTextInputField
                        title="Mobile"
                        type="tel"
                        placeholder="Enter mobile number"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        inputBorder="border-border-subtle rounded-lg"
                      />
                      <CountriesDropdown
                        listOfValues={PROVINCE_OPTIONS}
                        value={province}
                        onChange={setProvince}
                        label="Province"
                        placeholder="Select province"
                        enableSearch
                        {...dropdownProps}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="px-[24px] pb-[24px] pt-[8px] shrink-0">
                {error ? (
                  <div className="mb-[14px] text-[12px] text-text-status-red bg-bg-status-red border border-border-button-delete rounded-lg p-[10px]">
                    {error}
                  </div>
                ) : null}

                <div className="flex flex-col gap-[8px] sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || loadingLists}
                    className="w-full shrink-0 sm:flex-1 sm:min-w-0 h-[48px] rounded-full text-white text-[14px] font-semibold disabled:opacity-60"
                    style={{
                      background:
                        "linear-gradient(90deg, #0E18A7 0%, #5E0EAF 51%, #AE0DB2 100%)",
                    }}
                  >
                    {submitting
                      ? "Processing..."
                      : flow === "offramp"
                        ? "Cash Out"
                        : "Purchase Now"}
                  </button>
                  <AccessInfoBadges className="mt-0 w-full sm:w-auto sm:shrink-0" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
